// Owner console look (D-066, owner 2026-10-25: "reconstruct the console like the vendor dashboard"):
// the vendor admin's cards (rounded, tinted border, soft shadow) and its section bar (a white bar of
// icon + label buttons; the active one is brand-tinted with a gradient icon and label, D-038/D-039).
import type { ReactNode } from "react";
import { cx, Icon, type IconName } from "@ady/ui";

export const card = "rounded-card border border-card bg-surface shadow-card";

// Chart axis labels: "2026-05" → "May 26", "2026-10-02" → "2 Oct" (not "26-05" / "10-02").
const MONTH = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  year: "2-digit",
  timeZone: "UTC",
});
const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
export const monthLabel = (m: string) => MONTH.format(new Date(`${m.slice(0, 7)}-15T00:00:00Z`));
export const dayLabel = (d: string) => DAY.format(new Date(`${d.slice(0, 10)}T00:00:00Z`));

/** The section bar's classes, for pages that build their own tab buttons. */
export const TAB_BAR =
  "ady-scroll-quiet flex w-fit max-w-full gap-1 overflow-x-auto rounded-xs border border-card bg-surface p-1";
export const TAB_BTN =
  "flex min-h-control flex-none items-center gap-2 rounded-xs px-3 text-sm font-medium";
// The brand gradient text needs its own element (it paints the background), so plain tabs use the link colour.
export const TAB_ON = "ady-bg-brand-tint text-link";
export const TAB_OFF = "text-fg-secondary hover:bg-hover";

export function SectionBar<T extends string>({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: [T, string, IconName][];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div role="tablist" aria-label={label} className={TAB_BAR}>
      {items.map(([id, text, icon]) => {
        const on = id === value;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(id)}
            className={cx(TAB_BTN, on ? "ady-bg-brand-tint" : TAB_OFF)}
          >
            <Icon name={icon} gradient={on} />
            <span className={on ? "ady-text-brand" : undefined}>{text}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Text inputs, selects and text areas on console forms. */
export const field =
  "min-h-control w-full rounded-xs border border-control bg-surface px-2.5 text-sm outline-none focus:outline-2 focus:outline-offset-2 focus:outline-focus";

/** A card with a small uppercase heading. */
export function Panel({
  title,
  children,
  aside,
  className,
}: {
  title: string;
  children: ReactNode;
  aside?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx(card, "flex flex-col gap-2 p-3", className)}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-2xs font-bold tracking-wide text-fg-tertiary uppercase">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** An on/off switch with a visible label. */
export function Toggle({
  on,
  onChange,
  label,
  help,
  testId,
  hideLabel,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  help?: string;
  testId?: string;
  /** Only the switch shows (a table column names it); the label stays for screen readers. */
  hideLabel?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-3">
      <span className={hideLabel ? "ady-sr-only" : "flex flex-col"}>
        <span className="text-xs font-semibold">{label}</span>
        {help ? <span className="text-2xs text-fg-tertiary">{help}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={() => onChange(!on)}
        data-testid={testId}
        className={cx(
          "relative mt-0.5 inline-flex h-4.5 w-8 flex-none items-center rounded-full transition-colors duration-(--ady-dur-quick)",
          on ? "ady-bg-brand" : "bg-sunken ring-1 ring-control",
        )}
      >
        <span
          className={cx(
            "inline-block size-3.5 rounded-full bg-surface shadow transition-transform duration-(--ady-dur-quick)",
            on ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
    </label>
  );
}

/** A filter pill with an optional count. */
export function Pill({
  on,
  onClick,
  children,
  count,
  icon,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  count?: number;
  icon?: IconName;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cx(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        on
          ? "border-link bg-selected text-link"
          : "border-control text-fg-secondary hover:bg-hover",
      )}
    >
      {icon ? <Icon name={icon} className="size-3" /> : null}
      {children}
      {count !== undefined ? (
        <span className="tabular-nums">· {count.toLocaleString("en-IN")}</span>
      ) : null}
    </button>
  );
}
